import React, { useState, useEffect } from 'react';
import { TestResult } from '../types';

interface EditTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    systemName: string;
    testerName: string;
    environment: string;
    version?: string;
    platforms: ('iOS' | 'Android' | 'Web')[];
  }) => void;
  testData: TestResult;
}

const EditTestModal: React.FC<EditTestModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  testData
}) => {
  const [formData, setFormData] = useState({
    systemName: '',
    testerName: '',
    environment: '',
    version: '',
    platforms: [] as ('iOS' | 'Android' | 'Web')[]
  });

  useEffect(() => {
    if (testData) {
      setFormData({
        systemName: testData.metadata.systemName || '',
        testerName: testData.metadata.testerName || '',
        environment: testData.metadata.environment || '',
        version: testData.metadata.version || '',
        platforms: testData.metadata.platforms || []
      });
    }
  }, [testData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
    onClose();
  };

  const handlePlatformChange = (platform: 'iOS' | 'Android' | 'Web') => {
    setFormData(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform]
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Edytuj test</h2>
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Nazwa systemu
              </label>
              <input
                type="text"
                value={formData.systemName}
                onChange={(e) => setFormData(prev => ({ ...prev, systemName: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Tester
              </label>
              <input
                type="text"
                value={formData.testerName}
                onChange={(e) => setFormData(prev => ({ ...prev, testerName: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Środowisko
              </label>
              <input
                type="text"
                value={formData.environment}
                onChange={(e) => setFormData(prev => ({ ...prev, environment: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Wersja
              </label>
              <input
                type="text"
                value={formData.version}
                onChange={(e) => setFormData(prev => ({ ...prev, version: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Platformy
              </label>
              <div className="flex gap-3">
                {(['iOS', 'Android', 'Web'] as const).map((platform) => (
                  <button
                    key={platform}
                    type="button"
                    onClick={() => handlePlatformChange(platform)}
                    className={`px-4 py-2 rounded-lg transition-all ${
                      formData.platforms.includes(platform)
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 dark:bg-slate-600 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-slate-500'
                    }`}
                  >
                    {platform}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-8 flex justify-end gap-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-gray-100 dark:bg-slate-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-500 transition-all"
            >
              Anuluj
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all"
            >
              Zapisz zmiany
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTestModal; 